import { ref, createRef } from "lit/directives/ref.js";
import { LitElement, html, nothing } from "lit";
import { usageWidgetStyles } from "js/sharedStyles/usageWidget.js";

class BCUsageWidget extends LitElement {
  static properties = {
    optionvaluesGas: {
      type: Array,
      reflect: true,
      attribute: "data-option-values-gas",
    },
    optionvaluesElectricity: {
      type: Array,
      reflect: true,
      attribute: "data-option-values-electricity",
    },
    preselectedValue: {
      type: Number,
      reflect: true,
      attribute: "data-preselected-value",
    },
    selectedPersons: {
      type: Number,
      reflect: true,
      attribute: "data-selected-persons",
    },
    showGuesstimateToggle: {
      type: Boolean,
      reflect: true,
      default: false,
      attribute: "data-show-guesstimate-toggle",
    },
    showGuesstimate: {
      type: Boolean,
      reflect: true,
      default: true,
    },
    usageSelection: {
      type: String,
      reflect: true,
      default: "guesstimate",
      attribute: "data-usage-selection",
    },
    showSingleDualToggle: {
      type: Boolean,
      reflect: true,
      default: false,
      attribute: "data-show-single-dual-toggle",
    },
    showGas: {
      type: Boolean,
      reflect: true,
      default: false,
      attribute: "data-show-gas",
    },
    lowUsage: {
      type: Number,
      reflect: true,
      default: 0,
      attribute: "data-low-usage",
    },
    highUsage: {
      type: Number,
      reflect: true,
      default: 0,
      attribute: "data-high-usage",
    },
    hoveringPersons: {
      state: true,
    },
    showDualUsage: {
      type: Boolean,
      reflect: true,
      default: false,
      attribute: "data-show-dual-usage",
    },
    hiddenUsage: {
      state: true,
    },
    hiddenHighUsageInput: {
      state: true,
    },
    hiddenLowUsageInput: {
      state: true,
    },
  };
  lowUsageInputRef = createRef();
  highUsageInputRef = createRef();
  totalInputRef = createRef();
  meterTypeRef = createRef();

  static styles = [usageWidgetStyles];
  constructor() {
    super();

    this.selectedPersons = 2;
    this.hoveringPersons = null;

    this.optionvalues = [];
    this.preselectedValue = null;

    this.hiddenHighUsageInput = document.getElementById("hiddenHighUsageInput");
    this.hiddenLowUsageInput = document.getElementById("hiddenLowUsageInput");
    this.hiddenUsage = document.getElementById("hiddenUsageInput");
    this.hiddenMeterType = document.getElementById("hiddenMeterType");
  }

  connectedCallback() {
    super.connectedCallback();

    this.optionvalues = this.showGas ? this.optionvaluesGas : this.optionvaluesElectricity;

    if (this.usageSelection) {
      this.showGuesstimate = this.usageSelection == "guesstimate";
    } else {
      this.showGuesstimate = this.showGuesstimateToggle;
    }

    if (this.optionvalues && this.preselectedValue) {
      this.usage = this.preselectedValue;
      this.hiddenUsage.value = this.preselectedValue;

      if (this.optionvalues.indexOf(this.preselectedValue) == -1) {
        let selectedPersons = 1;
        this.optionvalues.forEach((value, index) => {
          if (value <= this.preselectedValue) {
            selectedPersons = index + 1;
          }
        });
        this.selectedPersons = selectedPersons;
      } else {
        this.selectedPersons = this.optionvalues.indexOf(this.preselectedValue) + 1;
      }
    } else if (this.optionvalues && this.showGuesstimateToggle) {
      const selectedUsage = this.optionvalues[this.selectedPersons - 1];

      this.usage = selectedUsage;
      this.hiddenUsage.value = selectedUsage;

      if (this.showSingleDualToggle) {
        this._setSplitUsage(selectedUsage);
      }
    } else {
      if (this.usage == undefined) {
        this.usage = "";
        this.hiddenUsage.value = "";

        if (this.highUsage != undefined && this.lowUsage != undefined) {
          this.usage = this.lowUsage + this.highUsage;
          this.hiddenUsage.value = this.usage;
        }
      } else {
        this.hiddenUsage.value = this.usage;
      }

      if (this.highUsage == undefined) {
        this.highUsage = "";
        this.hiddenHighUsageInput.value = "";
      } else {
        this.hiddenHighUsageInput.value = this.highUsage;
      }

      if (this.lowUsage == undefined) {
        this.lowUsage = "";
        this.hiddenLowUsageInput.value = "";
      } else {
        this.hiddenLowUsageInput.value = this.lowUsage;
      }
    }
    if (this.showSingleDualToggle) {
      this.meterType = this.showDualUsage ? "dual" : "single";
      this.hiddenMeterType.value = this.meterType;
    }
  }

  _handleSelection(person) {
    this.selectedPersons = person;
    const selectedUsage = this.optionvalues[person - 1];
    this.usage = selectedUsage;
    this.hiddenUsage.value = selectedUsage;
    if (this.showSingleDualToggle) {
      this._setSplitUsage(selectedUsage);
    }
    this.hiddenUsage.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _usageInputCheck(inputRef) {
    let inputElement = inputRef.value;
    let usage = inputElement.value;

    usage = usage.replace(/[^0-9]/g, ""); // Remove non-numeric characters
    inputElement.value = usage; // Update the input value
  }

  _updateSingleUsage() {
    let usage = this.totalInputRef.value.value;
    this.hiddenUsage.value = usage;
    if (this.showSingleDualToggle) {
      this._setSplitUsage(usage);
    }
    this.hiddenUsage.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _updateDualUsage() {
    let highUsage = this.highUsageInputRef.value.value;
    let lowUsage = this.lowUsageInputRef.value.value;
    this.hiddenHighUsageInput.value = highUsage;
    this.hiddenLowUsageInput.value = lowUsage;

    let usage = parseInt(highUsage) + parseInt(lowUsage);

    if (isNaN(usage)) {
      usage = "";
    }

    this.usage = usage;
    this.hiddenUsage.value = usage;

    if (usage != "") {
      this.hiddenUsage.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  _setSplitUsage(selectedUsage) {
    if (selectedUsage) {
      let tenthUsage = Math.floor(selectedUsage / 10);
      this.usage = selectedUsage;
      this.highUsage = tenthUsage * 6;
      this.lowUsage = tenthUsage * 4;

      this.hiddenUsage.value = this.usage;
      this.hiddenHighUsageInput.value = this.highUsage;
      this.hiddenLowUsageInput.value = this.lowUsage;

      this.hiddenUsage.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  _toggleGuesstimate() {
    this.showGuesstimate = !this.showGuesstimate;
    document.getElementById("usageSelection").value = this.showGuesstimate ? "guesstimate" : "own";
  }
  toggleSingleDual() {
    this.showDualUsage = this.meterTypeRef.value.checked;
    hiddenMeterType.value = this.showDualUsage ? "dual" : "single";
  }

  render() {
    this.optionvalues = this.showGas ? this.optionvaluesGas : this.optionvaluesElectricity;

    return html`
      <div class="u-font-weight--bold">Jährlicher ${this.showGas ? "Gasverbrauch" : "Stromverbrauch"}</div>
      <div>${this.showSingleDualToggle ? html`${this.renderSingleDualToggle(this.showDualUsage)}` : nothing}</div>
      <div>${this.renderSelectionType()}</div>
      <div>${this.showGuesstimateToggle ? html`${this.renderGuesstimateToggle()}` : nothing}</div>
    `;
  }
  renderGuesstimateToggle() {
    return html` <button @click="${() => this._toggleGuesstimate()}" class="c-link c-link--accent">${this.showGuesstimate ? "Eigenen Verbrauch eingeben" : "Einen Schätzwert machen"}</button>`;
  }

  renderSingleDualToggle(showDualUsage) {
    return html`
      <div class="mb--">
        <input class="c-inputfields__input--checkbox" id="meterType" type="checkbox" ?checked=${showDualUsage} @change=${() => this.toggleSingleDual()} ${ref(this.meterTypeRef)} />
        <label for="meterType" class="c-inputfields__label"> Ich hab ein Doppeltarifzähler </label>
      </div>
    `;
  }

  renderSelectionType() {
    if (this.showGuesstimate) {
      return this.renderPersons();
    } else {
      if (this.showDualUsage) {
        return this.renderDoubleUsageInputs();
      } else {
        return this.renderSingleUsageInput();
      }
    }
  }
  renderPersons() {
    return html`<div class="c-person-selector__wrapper">
      ${this.renderPersonCountSelector()}
      <div class="c-person-selector__label">
        <span class="c-person-selector__label--person"> ${this.selectedPersons} ${this.selectedPersons == 1 ? "Person" : " Personen"}</span>
        ${!this.showGas ? html`<span class="c-person-selector__label--electricity" @click="${() => this._toggleGuesstimate()}"> ${this.renderBolt()} ${this.optionvalues[this.selectedPersons - 1]} kWh </span>` : html`<span class="c-person-selector__label--gas" @click="${() => this._toggleGuesstimate()}"> ${this.renderFlame()} ${this.optionvalues[this.selectedPersons - 1]} kWh </span>`}
      </div>
    </div>`;
  }
  renderDoubleUsageInputs() {
    return html` <div class="layout mb--">
      <div class="layout__item u-1/2">
        <label for="usageHigh" class="u-size-s">Verbrauch HT</label>
        <div class="c-inputwidget__usage">
          <input type="text" class="c-inputwidget__electricity-input" maxlength="5" @keyup="${() => this._usageInputCheck(this.highUsageInputRef)}" @change="${() => this._updateDualUsage()}" .value=${String(this.highUsage)} ${ref(this.highUsageInputRef)} name="usageHigh" id="usageHigh" />
          <span class="c-inputwidget__usage-unit">kWh</span>
        </div>
      </div>

      <div class="layout__item u-1/2">
        <label for="usageLow" class="u-size-s">Verbrauch NT</label>
        <div class="c-inputwidget__usage">
          <input type="text" class="c-inputwidget__electricity-input" maxlength="5" @keyup="${() => this._usageInputCheck(this.lowUsageInputRef)}" @change="${() => this._updateDualUsage()}" .value=${String(this.lowUsage)} ${ref(this.lowUsageInputRef)} name="usageLow" id="usageLow" />
          <span class="c-inputwidget__usage-unit">kWh</span>
        </div>
      </div>
    </div>`;
  }
  renderSingleUsageInput() {
    return html` <div class="u-display--flex u-gap mb--">
      <div class="c-inputwidget__usage">
        <input type="text" class="${this.showGas ? "c-inputwidget__gas-input" : "c-inputwidget__electricity-input"}" maxlength="5" @keyup="${() => this._usageInputCheck(this.totalInputRef)}" @change="${() => this._updateSingleUsage()}" .value=${String(this.usage)} ${ref(this.totalInputRef)} name="usage" id="usage" />
        <span class="c-inputwidget__usage-unit">kWh</span>
      </div>
    </div>`;
  }

  renderPersonCountSelector() {
    const personCount = Array.from({ length: 5 }, (_, i) => i + 1);
    return html` <div class="c-person-selector">
      ${personCount.map((person) => {
        const isSelectedClass = person <= this.selectedPersons ? "c-person-selector__person-selected" : "";
        return html`<button type="button"
            @click="${() => this._handleSelection(person)}"
            class="
            c-person-selector__person
            ${isSelectedClass}"
            data-person=${person}
            aria-label="Select ${isSelectedClass} person"
            >${this.renderPerson(person)}
          </button></div></div>`;
      })}
    </div>`;
  }
  renderFlame() {
    return html` <svg width="10" height="15" viewBox="0 0 10 15" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.013 0S4.771 3.251 2.5 5.558C.481 7.195.158 8.065.033 9.299c-.126 1.24.01 3.636 2.283 5.271 0 0-.817-1.99 1.196-3.623C5.43 9.389 5.194 8.282 5.165 8.18c.023.086.478 1.713 1.474 2.638 1.021.949.872 2.627-.094 4.182 0 0 3.14-2.023 3.42-4.735.128-1.262-.102-2.582-.53-3.84C8.365 3.276 6.057.511 5.013 0z" fill="#9b9b9b" class="u-svg-fill" />
    </svg>`;
  }
  renderBolt() {
    return html` <svg width="9" height="15" viewBox="0 0 9 15" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.477 5.145A.25.25 0 0 0 8.25 5H5.288L8.21.384A.25.25 0 0 0 8.001 0H4a.25.25 0 0 0-.225.138l-3.75 7.5A.25.25 0 0 0 .25 8h2.57l-2.8 6.653a.25.25 0 0 0 .421.258l8-9.5a.25.25 0 0 0 .036-.266z" fill="#9b9b9b" />
    </svg>`;
  }
  renderPerson(person) {
    return html`<svg width="13" height="30" data-person=${person} viewBox="0 0 13 30" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.513 16.105c.19.876-.314 1.592-1.123 1.592H9.926l-.134 1.625-.718 9.054C9.003 29.269 8.332 30 7.58 30H4.852c-.752 0-1.424-.731-1.495-1.624l-.718-9.054-.137-1.625H1.103c-.774 0-1.252-.716-1.061-1.592l1.45-6.683c.19-.876 1.079-1.592 1.975-1.592h5.621c.897 0 1.785.716 1.975 1.592l1.45 6.683L6.84 1.921m2.687 1.33a3.25 3.25 0 1 0-6.5-.001 3.25 3.25 0 0 0 6.5 0"></path>
    </svg>`;
  }
}

const initBCUsageWidget = () => {
  if (!window.customElements.get("bc-person-selector")) {
    customElements.define("bc-usage-widget", BCUsageWidget);
  }
};

export { initBCUsageWidget };
